import { useState } from "react";
import { Flex } from "styles/utility-components";

import { generateContactsUrl } from "components/contacts/utils";
import FindEntity from "components/entities/FindEntity";
import { OutreachBuilderModal } from "components/outreach_builder/OutreachBuilderModal";
import { LeadDetails } from "lib/hooks/api/leads/useLead";
import { EntityActionParams } from "lib/hooks/api/leads/useLeadEntityActions";
import { useSubscription } from "lib/hooks/subscription";
import { TrackingProvider } from "lib/tracking";
import { ContactSource } from "lib/types/graphQLEnums";
import { LeadSavedContactCard, SavedContact } from "./LeadSavedContactCard";
import { LeadEmptyRow, LeadLoadingRow, LeadSection, LeadSectionGroup } from "./LeadSection";
import { SuggestedContactsCard } from "./SuggestedContactsCard";
import { TeasedContactsCard } from "./TeasedContactsCard";

// Saved and recommended cards send the same contact events, so each group names itself.
const SAVED_CONTEXT = { "Context source": "Lead saved contacts" };
const RECOMMENDED_CONTEXT = { "Context source": "Lead recommendations" };

export function LeadContactsSection({
  lead,
  showSuggestions,
  isGeneratingSuggestions,
  removeEntity,
}: {
  lead: NonNullable<LeadDetails>;
  /** Suggestions are behind a POC flag, so the group can be absent entirely. */
  showSuggestions: boolean;
  /**
   * A first batch is on its way — the workflow is running, or the page has just asked for one.
   * Shows progress in place of the (empty) list.
   */
  isGeneratingSuggestions: boolean;
  removeEntity: (entity: EntityActionParams) => void;
}) {
  const subscription = useSubscription();
  const [outreachContact, setOutreachContact] = useState<SavedContact | null>(null);
  const suggestions = lead.suggestedContacts;
  const hasSuggestions = suggestions.length > 0;
  const hasBuyer = (lead.buyers?.length ?? 0) > 0;
  const savedContactsUrl = generateContactsUrl({
    buyerGuids: lead.buyers?.map((buyer) => buyer.id) ?? [],
  });

  const hasContactsAccess = subscription.hasDataTypes("CONTACTS");
  const showRecommendations = showSuggestions && hasContactsAccess;
  // Suggestions are generated for unentitled companies too, so the lead can show what the
  // feature does. Only ever a tease: the server redacts the identifying fields.
  const teaseRecommendations = showSuggestions && !hasContactsAccess;

  const isSearching = !hasSuggestions && isGeneratingSuggestions;

  // Lives on the section's title row rather than under the list — the section header is the one
  // place to add a contact, whether or not the lead already has some.
  const findContacts = <FindEntity entity="contact" url={savedContactsUrl} variant="inline" />;

  const savedContacts = (
    <TrackingProvider data={SAVED_CONTEXT}>
      <Flex column gap={12}>
        {lead.contacts.map((contact) => (
          <LeadSavedContactCard
            key={contact.id}
            contact={contact}
            onOutreachClick={() => setOutreachContact(contact)}
            onRemove={() =>
              removeEntity({
                // A Cognism contact is a `contact_cognism` entity; its `id` is the stored entityId
                // (the cognism_redeemed_contact row), so removal still keys on id.
                entityType:
                  contact.source === ContactSource.Cognism ? "contact_cognism" : "contact",
                entityId: contact.id,
              })
            }
          />
        ))}
      </Flex>
    </TrackingProvider>
  );

  const outreachModal = (
    <OutreachBuilderModal
      isOpen={!!outreachContact}
      onClose={() => setOutreachContact(null)}
      buyerName={outreachContact?.buyer?.name ?? ""}
      contactEmail={outreachContact?.email ?? ""}
      contactName={[outreachContact?.firstName, outreachContact?.lastName]
        .filter(Boolean)
        .join(" ")}
      jobTitle={outreachContact?.jobTitle ?? ""}
    />
  );

  if (!showRecommendations) {
    return (
      <LeadSection title="Contacts" actions={findContacts}>
        {savedContacts}
        {/* The generating wait is identical whether or not the company is entitled — only the
            result is redacted — so it gets the same progress row. Nothing is shown once the batch
            comes back empty: there is no feature to tease with no contacts to tease it with. */}
        {teaseRecommendations && (hasSuggestions || isSearching) && (
          <LeadSectionGroup
            label={hasSuggestions ? `Recommended (${suggestions.length})` : "Recommended"}
          >
            {hasSuggestions ? (
              <TrackingProvider data={RECOMMENDED_CONTEXT}>
                <TeasedContactsCard suggestedContacts={suggestions} />
              </TrackingProvider>
            ) : (
              <LeadLoadingRow>Searching for contacts</LeadLoadingRow>
            )}
          </LeadSectionGroup>
        )}
        {outreachModal}
      </LeadSection>
    );
  }

  return (
    <LeadSection title="Contacts" actions={findContacts}>
      {/* Labelled only when there is something to label — an empty "Saved (0)" group would read as
          a section that failed to load. */}
      {lead.contacts.length > 0 && (
        <LeadSectionGroup label={`Saved (${lead.contacts.length})`}>
          {savedContacts}
        </LeadSectionGroup>
      )}
      {/* No count while searching or empty — there isn't one to show. */}
      <LeadSectionGroup
        label={hasSuggestions ? `Recommended (${suggestions.length})` : "Recommended"}
      >
        <TrackingProvider data={RECOMMENDED_CONTEXT}>
          <Recommendations
            leadId={lead.id}
            suggestions={suggestions}
            isSearching={isSearching}
            hasBuyer={hasBuyer}
          />
        </TrackingProvider>
      </LeadSectionGroup>
      {outreachModal}
    </LeadSection>
  );
}

function Recommendations({
  leadId,
  suggestions,
  isSearching,
  hasBuyer,
}: {
  leadId: string;
  suggestions: NonNullable<LeadDetails>["suggestedContacts"];
  isSearching: boolean;
  hasBuyer: boolean;
}) {
  if (suggestions.length > 0) {
    return <SuggestedContactsCard leadId={leadId} suggestedContacts={suggestions} />;
  }

  if (isSearching) {
    return <LeadLoadingRow>Searching for contacts</LeadLoadingRow>;
  }

  if (!hasBuyer) {
    return <LeadEmptyRow>Add a buyer to this lead to see recommended contacts</LeadEmptyRow>;
  }

  return <LeadEmptyRow>No contact recommendations for this lead</LeadEmptyRow>;
}
